import { Link } from "react-router-dom";
import { ArrowRight, Download, CalendarCheck, Compass } from "lucide-react";
import { IMAGES, STATS, ARCHITECTURE, STRANDS } from "../data/content";
import { useModals } from "../components/ModalContext";

function Overline({ children, light }) {
  return (
    <p className={`text-xs font-bold uppercase tracking-[0.25em] ${light ? "text-gold" : "text-gold-700"}`}>
      {children}
    </p>
  );
}

export default function Home() {
  const { openProspectus } = useModals();

  return (
    <main data-testid="home-page">
      {/* Hero */}
      <section className="relative isolate overflow-hidden bg-navy" aria-label="Introduction">
        <img
          src={IMAGES.hero}
          alt="Group of school students learning together outdoors"
          className="absolute inset-0 -z-10 h-full w-full object-cover opacity-35"
        />
        <div className="absolute inset-0 -z-10 bg-gradient-to-r from-navy via-navy/85 to-navy/40" aria-hidden="true" />
        <div className="mx-auto max-w-7xl px-4 py-24 sm:px-6 sm:py-32 lg:px-8 lg:py-40">
          <div className="max-w-2xl animate-fade-up">
            <Overline light>The Experiential Education Foundation</Overline>
            <h1 className="mt-4 font-serif text-4xl font-bold leading-[1.1] tracking-tight text-white sm:text-5xl lg:text-6xl" data-testid="hero-heading">
              The World is <span className="text-gold">OUR</span> Curriculum
            </h1>
            <p className="mt-6 text-base leading-relaxed text-slate-200 sm:text-lg">
              As a non-profit leader in curriculum aligned experiential learning, The Experiential Education Foundation
              turns world environments into active classrooms, builds defensible skills and student portfolios, and
              prepares future leaders.
            </p>
            <p className="mt-4 text-sm leading-relaxed text-slate-300 sm:text-base">
              With a collective wisdom of retired civil servants, leading academicians, social thinkers and teachers as
              our advisory board, we bring a shared vision to life, converting academic instruction into immersive,
              life-shaping experiential learning.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to="/learning-journey#ib"
                data-testid="hero-ib-pathway"
                className="inline-flex items-center gap-2 rounded-md bg-gold px-5 py-3 text-xs font-bold uppercase tracking-wider text-navy transition-colors hover:bg-gold-hover"
              >
                IB <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
              <Link
                to="/learning-journey#framework"
                data-testid="hero-cbse-pathway"
                className="inline-flex items-center gap-2 rounded-md border border-white/40 px-5 py-3 text-xs font-bold uppercase tracking-wider text-white transition-colors hover:border-gold hover:text-gold"
              >
                CBSE / ICSE / ISC <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
              <button
                type="button"
                onClick={openProspectus}
                data-testid="hero-download-prospectus-button"
                className="inline-flex items-center gap-2 px-4 py-3 text-xs font-bold uppercase tracking-wider text-white underline decoration-gold decoration-2 underline-offset-4 transition-colors hover:text-gold"
              >
                <Download className="h-4 w-4" aria-hidden="true" /> Download Prospectus
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Flagship journeys stats */}
      <section className="border-b border-slate-200 bg-white" aria-label="Flagship journeys at a glance">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <Overline>Flagship Journeys</Overline>
          <h2 className="mt-3 font-serif text-2xl font-semibold tracking-tight text-navy sm:text-3xl lg:text-4xl" data-testid="journey-begins-heading">
            Your Learning Journey Begins Here
          </h2>
          <p className="mt-2 text-base text-slate-600 sm:text-lg">Make the world your curriculum.</p>
          <dl className="mt-10 grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-slate-200 bg-slate-200 lg:grid-cols-4" data-testid="stats-band">
            {STATS.map((s) => (
              <div key={s.label} className="flex flex-col items-center justify-center bg-ivory px-4 py-7 text-center">
                <dd className="order-1 font-serif text-3xl font-bold text-navy sm:text-4xl">{s.value}</dd>
                <dt className="order-2 mt-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                  {s.label}
                </dt>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* Academic architecture */}
      <section className="bg-parchment" aria-label="The academic architecture">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <Overline>The library at a glance</Overline>
          <h2 className="mt-3 font-serif text-2xl font-semibold tracking-tight text-navy sm:text-3xl lg:text-4xl" data-testid="architecture-heading">
            The Academic Architecture
          </h2>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {ARCHITECTURE.map((card, i) => (
              <article
                key={card.title}
                className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
                data-testid={`architecture-card-${i}`}
              >
                <span className="font-serif text-3xl font-bold text-gold" aria-hidden="true">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="mt-4 text-lg font-semibold text-navy">{card.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{card.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Five strands */}
      <section className="bg-navy" aria-label="The five strands of a CASIA journey">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <Overline light>CASIA Methodology</Overline>
          <h2 className="mt-3 max-w-2xl font-serif text-2xl font-semibold tracking-tight text-white sm:text-3xl lg:text-4xl" data-testid="strands-heading">
            The Five Strands of a CASIA Journey
          </h2>
          <p className="mt-3 max-w-2xl text-base text-slate-300">
            Every journey is designed to weave five strands into one full act of learning.
          </p>
          <div className="mt-12 divide-y divide-white/10 border-y border-white/10">
            {STRANDS.map((s, i) => (
              <div key={s.title} className="grid gap-4 py-8 sm:grid-cols-[80px_1fr_2fr] sm:items-start sm:gap-8" data-testid={`strand-${i}`}>
                <span className="font-serif text-5xl font-bold text-gold" aria-hidden="true">{s.letter}</span>
                <div>
                  <h3 className="text-xl font-semibold text-white">{s.title}</h3>
                  <p className="text-sm italic text-gold">{s.subtitle}</p>
                </div>
                <p className="text-sm leading-relaxed text-slate-300 sm:text-base">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why choose / co-design */}
      <section className="bg-ivory" aria-label="Why choose The Experiential Education Foundation">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 py-20 sm:px-6 lg:grid-cols-2 lg:px-8">
          <div>
            <Overline>Why choose The Experiential Education Foundation?</Overline>
            <blockquote className="mt-6 border-l-4 border-gold pl-6 font-serif text-xl font-medium italic leading-relaxed text-navy sm:text-2xl" data-testid="codesign-quote">
              “We believe your school’s unique vision is not our template, but our starting point. We don’t offer
              pre-packaged travel; we partner with you to co-design a learning journey, turning abstract curriculum
              standards into life-shaping, real-world learning experiences.”
            </blockquote>
            <div className="mt-10 grid gap-4 sm:grid-cols-1">
              <Link
                to="/learning-journey"
                data-testid="cta-transform-journey"
                className="group flex items-center justify-between rounded-lg border border-slate-200 bg-white p-5 transition-all duration-300 hover:-translate-y-1 hover:border-gold hover:shadow-lg"
              >
                <span className="flex items-center gap-4">
                  <Compass className="h-6 w-6 text-gold" aria-hidden="true" />
                  <span className="font-semibold text-navy">Learn how CASIA can transform your learning journey</span>
                </span>
                <ArrowRight className="h-5 w-5 text-slate-400 transition-transform group-hover:translate-x-1 group-hover:text-gold" aria-hidden="true" />
              </Link>
              <Link
                to="/contact"
                data-testid="cta-meet-with-us"
                className="group flex items-center justify-between rounded-lg border border-slate-200 bg-white p-5 transition-all duration-300 hover:-translate-y-1 hover:border-gold hover:shadow-lg"
              >
                <span className="flex items-center gap-4">
                  <CalendarCheck className="h-6 w-6 text-gold" aria-hidden="true" />
                  <span className="font-semibold text-navy">Meet With Us</span>
                </span>
                <ArrowRight className="h-5 w-5 text-slate-400 transition-transform group-hover:translate-x-1 group-hover:text-gold" aria-hidden="true" />
              </Link>
              <button
                type="button"
                onClick={openProspectus}
                data-testid="cta-prospectus-download"
                className="group flex items-center justify-between rounded-lg border border-slate-200 bg-white p-5 text-left transition-all duration-300 hover:-translate-y-1 hover:border-gold hover:shadow-lg"
              >
                <span className="flex items-center gap-4">
                  <Download className="h-6 w-6 text-gold" aria-hidden="true" />
                  <span className="font-semibold text-navy">Prospectus Download</span>
                </span>
                <ArrowRight className="h-5 w-5 text-slate-400 transition-transform group-hover:translate-x-1 group-hover:text-gold" aria-hidden="true" />
              </button>
            </div>
          </div>
          <div className="relative">
            <img
              src={IMAGES.fieldEcology}
              alt="Students engaged in outdoor field research and ecological study"
              className="w-full rounded-lg object-cover shadow-xl aspect-[4/5] sm:aspect-[4/4]"
            />
            <div className="absolute -bottom-6 -left-6 hidden rounded-lg bg-navy p-6 shadow-xl sm:block" aria-hidden="true">
              <p className="font-serif text-3xl font-bold text-gold">17/17</p>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-300">SDGs Mapped</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-navy" aria-label="Get started">
        <div className="mx-auto max-w-7xl px-4 py-16 text-center sm:px-6 lg:px-8">
          <h2 className="font-serif text-2xl font-semibold text-white sm:text-3xl" data-testid="final-cta-heading">
            Begin your school’s learning journey with CASIA
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-sm text-slate-300 sm:text-base">
            Co-designed, delivered, documented and evaluated. Curriculum journeys built around your school’s vision.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <button
              type="button"
              onClick={openProspectus}
              data-testid="footer-cta-prospectus-button"
              className="inline-flex items-center gap-2 rounded-md bg-gold px-6 py-3 text-xs font-bold uppercase tracking-wider text-navy transition-colors hover:bg-gold-hover"
            >
              <Download className="h-4 w-4" aria-hidden="true" /> Download Prospectus
            </button>
            <Link
              to="/contact"
              data-testid="footer-cta-contact-link"
              className="inline-flex items-center gap-2 rounded-md border border-white/40 px-6 py-3 text-xs font-bold uppercase tracking-wider text-white transition-colors hover:border-gold hover:text-gold"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
